import React, {useState} from 'react';
import styled from 'styled-components';
import {Button} from '@betareader/design-system';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';

import AppIcon from '../../components/AppIcon';
import LanguageGetter from '../user/userProfileBuilder/fields/languages';
import {updateSearchFilters} from '../../modules/betaOpportunities';
import useOutsideClick from '../../hooks/useOutsideClick';

const aiOptions = [
  ['all', 'All manuscripts'],
  ['human', 'Human-written'],
  ['assisted', 'AI-assisted'],
  ['generated', 'Contains AI-generated text'],
  ['no_generated', 'Declared without generated text or translation'],
  ['undeclared', 'Not yet declared'],
];

const FilterWrapper = styled.div`
  position: relative;
  flex: none;
  .br-discover-filter-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 30;
    width: min(320px, calc(100vw - 32px));
    padding: 16px;
    background: var(--br-surface);
    color: var(--br-text);
    border: 1px solid var(--br-border);
    border-radius: var(--br-card-radius);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.16);
  }
  .br-discover-filter-panel label {
    display: grid;
    gap: 6px;
    font-weight: 700;
  }
  .br-discover-filter-panel select {
    min-height: 40px;
    width: 100%;
    padding: 8px;
    border: 1px solid var(--br-border);
    border-radius: var(--br-control-radius);
    background: var(--br-surface);
    color: var(--br-text);
    font: inherit;
  }
`;

export const DiscoverFilter = ({t}) => {
  const dispatch = useDispatch();
  const hideAiGenerated = useSelector(
    state => state.user?.userProfile?.showAiGeneratedManuscripts === false);
  const searchFilters = useSelector(state => state.betaOpportunities.searchFilters) || {};
  const [open, setOpen] = useState(false);
  const language = searchFilters.language || [];
  const filterRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const panelId = React.useId();

  useOutsideClick(filterRef, () => setOpen(false));

  const handleLanguageChange = languages => {
    dispatch(updateSearchFilters({
      ...searchFilters,
      language: languages.filter(item => item !== null),
    }));
  };

  const hasFilters = language.length > 0 || !!searchFilters.ai || hideAiGenerated;

  const handleAiChange = event => {
    const value = event.target.value;
    try { localStorage.setItem('br-discovery-ai', value); } catch (_) {}
    dispatch(updateSearchFilters({
      ...searchFilters,
      ai: value === 'all' ? undefined : value,
    }));
  };

  return (
    <FilterWrapper ref={filterRef} onKeyDown={event => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }}>
      <Button ref={buttonRef} variant='ghost' size='small' type='button'
        aria-label='Search filters' aria-expanded={open} aria-controls={panelId}
        onClick={() => setOpen(previous => !previous)}>
        <AppIcon name={open ? 'chevron up' : 'chevron down'} aria-hidden='true' />
        {hasFilters && <span aria-hidden='true'>•</span>}
      </Button>
      {open && (
        <div className='br-discover-filter-panel' id={panelId}
          role='group' aria-label='Search filters'>
          <label>
            Manuscript AI use
            <select value={searchFilters.ai || 'all'} onChange={handleAiChange}>
              {aiOptions.map(([value, text]) => (
                <option key={value} value={value}
                  disabled={hideAiGenerated && value === 'generated'}>
                  {text}
                </option>
              ))}
            </select>
          </label>
          {hideAiGenerated && <p>{t('AiManuscriptPreferenceHidden')}</p>}
          <p><Link to='/account/profile'>{t('AiManuscriptPreferenceSettings')}</Link></p>
          <LanguageGetter
            header={t('Languages')}
            subheader={false}
            initialValue={language}
            onUpdate={handleLanguageChange}
            autoUpdate
          />
        </div>
      )}
    </FilterWrapper>
  );
};

export default withTranslation()(DiscoverFilter);
